import { AttachmentsField, uploadPending } from "./Attachments";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError, errMsg, newIdemKey, type Account, type Scope, type Transfer, type TransferQuote, type TransferResult } from "../api";
import { useEntities } from "../entity";
import { fmtDay, formatMoney, normalizeMoneyInput } from "../lib/money";
import { useMe } from "../session";
import { Modal } from "./Modal";
import { ScopeDialog, useRepeat } from "./Recurrence";
import { ImpactList } from "./TxForm";
import { Btn, Field, Input, Notice, Select } from "./ui";

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const positive = (n: string | null) => n !== null && !n.startsWith("-") && !/^0+(\.0+)?$/.test(n);

/** Bloco da taxa efetiva: SEMPRE calculada pelo servidor a partir dos dois valores; a tela não faz conta. */
export function RateInfo({ rateDisplay, unit = "BRL por 1 USD" }: { rateDisplay: string | null; unit?: string | null }) {
  if (!rateDisplay) return null;
  return <div className="rate" data-rate>Taxa efetiva: <strong className="tabular">{rateDisplay.replace(".", ",")}</strong> {unit}</div>;
}

/**
 * Transferência entre contas da mesma entidade. Moedas iguais: um valor. BRL↔USD: valor debitado na origem e valor
 * efetivamente creditado no destino; a taxa efetiva é calculada pelo servidor e apenas exibida (DEC-060).
 */
export function TransferForm({ transfer, onClose }: { transfer: Transfer | null; onClose: () => void }) {
  const { me } = useMe();
  const realm = me.user.realm;
  const { current } = useEntities();
  const entityId = transfer?.entityId ?? current?.id ?? "";
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [fromId, setFromId] = useState(transfer?.fromAccountId ?? "");
  const [toId, setToId] = useState(transfer?.toAccountId ?? "");
  const [fromAmount, setFromAmount] = useState(transfer ? transfer.fromAmount.decimal.replace(".", ",") : "");
  const [toAmount, setToAmount] = useState(transfer && transfer.crossCurrency ? transfer.toAmount.decimal.replace(".", ",") : "");
  const [description, setDescription] = useState(transfer?.description ?? "");
  const [plannedDate, setPlannedDate] = useState(transfer?.plannedDate ?? today());
  const [done, setDone] = useState(transfer ? transfer.status === "confirmado" : false);
  const [settledDate, setSettledDate] = useState(transfer?.settledDate ?? today());
  const [notes, setNotes] = useState(transfer?.notes ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [dup, setDup] = useState(false);
  const [atyp, setAtyp] = useState<{ rateDisplay: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState<TransferQuote | null>(null);
  const [quoteErr, setQuoteErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<TransferResult | null>(null);
  const [pending, setPending] = useState<File[]>([]);
  const [attachFail, setAttachFail] = useState<string[]>([]);
  const rep = useRepeat();
  const [askScope, setAskScope] = useState(false);
  const askRef = useRef(false); askRef.current = askScope;
  const scopeRef = useRef<Scope | null>(null);
  const closeForm = useCallback(() => { if (!askRef.current) onClose(); }, [onClose]);   // Esc com a pergunta aberta fecha só a pergunta
  const idem = useRef(newIdemKey());
  const lockedSettle = transfer ? transfer.status === "planejado" : false;

  useEffect(() => {
    void api<Account[]>("GET", `/api/${realm}/accounts?entityId=${entityId}&status=ativa`).then((a) => {
      setAccounts(a);
      if (!transfer) { setFromId((c) => c || a[0]?.id || ""); setToId((c) => c || a[1]?.id || ""); }
    }).catch((e) => setErr(errMsg(e)));
  }, [realm, entityId, transfer]);
  // contas atuais de uma transferência podem estar inativas: mantém as opções
  useEffect(() => {
    if (!transfer || !accounts.length) return;
    const add: Account[] = [];
    if (!accounts.some((a) => a.id === transfer.fromAccountId)) add.push({ id: transfer.fromAccountId, name: transfer.fromAccountName, currency: transfer.fromCurrency } as Account);
    if (!accounts.some((a) => a.id === transfer.toAccountId)) add.push({ id: transfer.toAccountId, name: transfer.toAccountName, currency: transfer.toCurrency } as Account);
    if (add.length) setAccounts((l) => [...l, ...add]);
  }, [transfer, accounts]);

  const from = accounts.find((a) => a.id === fromId);
  const to = accounts.find((a) => a.id === toId);
  const cross = !!from && !!to && from.currency !== to.currency;
  // DEC-112: limite mínimo visível = saldo inicial mais recente entre as duas contas (mesma regra do servidor); a efetiva também não pode ser futura
  const floorAcc = [from, to].reduce<Account | undefined>((m, a) => (a?.initialBalanceDate && (!m || a.initialBalanceDate > m.initialBalanceDate) ? a : m), undefined);
  const floor = floorAcc?.initialBalanceDate;
  const fromNorm = normalizeMoneyInput(fromAmount);
  const toNorm = normalizeMoneyInput(toAmount);

  // cotação: o servidor devolve a taxa efetiva a partir dos dois valores
  useEffect(() => {
    setQuote(null); setQuoteErr(null);
    if (!fromId || !toId || fromId === toId || !positive(fromNorm) || (cross && !positive(toNorm))) return;
    const h = setTimeout(() => {
      const qs = new URLSearchParams({ fromAccountId: fromId, toAccountId: toId, fromAmount: fromNorm! });
      if (cross) qs.set("toAmount", toNorm!);
      void api<TransferQuote>("GET", `/api/${realm}/transfers/quote?${qs}`).then(setQuote).catch((e) => setQuoteErr(errMsg(e)));
    }, 300);
    return () => clearTimeout(h);
  }, [realm, fromId, toId, fromNorm, toNorm, cross]);

  async function submit(e: FormEvent | null, confirmDuplicate = false, confirmAtypical = false, chosen?: Scope) {
    e?.preventDefault();
    if (chosen) scopeRef.current = chosen;
    setErr(null);
    if (!fromId || !toId) return setErr("Escolha a conta de origem e a de destino.");
    if (fromId === toId) return setErr("Origem e destino devem ser contas diferentes.");
    if (!positive(fromNorm)) return setErr("Informe o valor debitado na origem (maior que zero), por exemplo 1.234,56.");
    if (cross && !positive(toNorm)) return setErr("Informe o valor efetivamente creditado no destino.");
    let repeat: Record<string, unknown> | undefined;
    if (!transfer) { const b = rep.build(); if (b.error) return setErr(b.error); repeat = b.repeat; }
    // ocorrência de série: a pergunta "somente esta / esta e as seguintes" é obrigatória antes de gravar
    if (transfer?.seriesId && !scopeRef.current) { setAskScope(true); return; }
    setBusy(true);
    try {
      let r: TransferResult;
      const common = { fromAmount: fromNorm!, ...(cross ? { toAmount: toNorm! } : {}), description: description.trim() || null, notes: notes.trim() || null };
      if (transfer) {
        r = await api<TransferResult>("PATCH", `/api/${realm}/transfers/${transfer.id}`, {
          version: transfer.version, fromAccountId: fromId, toAccountId: toId, ...common, plannedDate, ...(done && !lockedSettle ? { settledDate } : {}),
          ...(confirmAtypical ? { confirmAtypicalRate: true } : {}), ...(transfer.seriesId && scopeRef.current === "following" ? { scope: "following" } : {}),
        });
      } else {
        if (confirmDuplicate || confirmAtypical) idem.current = newIdemKey();
        r = await api<TransferResult>("POST", `/api/${realm}/transfers`, {
          fromAccountId: fromId, toAccountId: toId, ...common, plannedDate: done ? settledDate : plannedDate, ...(done ? { settledDate } : {}),
          ...(confirmDuplicate ? { confirmDuplicate: true } : {}), ...(confirmAtypical ? { confirmAtypicalRate: true } : {}), ...(repeat ? { repeat } : {}),
        }, idem.current);
      }
      if (!transfer && pending.length) setAttachFail(await uploadPending(realm, "transfer", r.id, pending));
      setSaved(r);
    } catch (x) {
      if (x instanceof ApiError && x.code === "possible_duplicate") { setDup(true); setErr(null); }
      else if (x instanceof ApiError && x.code === "atypical_rate") { setAtyp({ rateDisplay: String((x as ApiError & { details?: { rateDisplay?: string } }).details?.rateDisplay ?? quote?.rateDisplay ?? "") }); setErr(null); }
      else setErr(errMsg(x));
    } finally { setBusy(false); setAskScope(false); }
  }

  if (saved) {
    return (
      <Modal title={transfer ? "Transferência atualizada" : "Transferência registrada"} onClose={onClose}>
        <Notice kind="ok">
          {saved.status === "planejado" ? "Planejada" : "Realizada"}: {saved.fromAccountName} → {saved.toAccountName} — {formatMoney(saved.fromAmount.decimal, saved.fromCurrency)}
          {saved.crossCurrency ? ` → ${formatMoney(saved.toAmount.decimal, saved.toCurrency)}` : ""}.
          {saved.series && ` Foram criadas ${saved.series.created} transferências de uma vez${saved.series.kind === "fixa" && saved.installmentTotal === null ? "; elas serão renovadas automaticamente ao final do período" : ""}.`}
          {saved.affected ? ` Também foram alteradas ${saved.affected} transferências seguintes.` : ""}
        </Notice>
        <RateInfo rateDisplay={saved.rateDisplay} />
        <ImpactList impacts={saved.impacts} />
        {attachFail.length > 0 && <Notice kind="error">A transferência foi salva, mas houve falha ao anexar: {attachFail.join(" ")} Abra “Editar” para anexar de novo.</Notice>}
        <div className="modal-actions"><Btn onClick={onClose} data-tr-close>Concluir</Btn></div>
      </Modal>
    );
  }

  return (
    <><Modal title={transfer ? "Editar transferência" : "Nova transferência"} onClose={closeForm} wide>
      <form onSubmit={(e) => void submit(e)} noValidate data-tr-form>
        {err && <Notice kind="error">{err}</Notice>}
        {dup && (
          <Notice kind="error">
            Já existe uma transferência igual (mesmas contas, valores e data). Deseja lançar mesmo assim?
            <div className="row" style={{ marginTop: 8 }}>
              <Btn variant="danger" onClick={(e) => { setDup(false); void submit(e as unknown as FormEvent, true); }} data-dup-confirm>Lançar mesmo assim</Btn>
              <Btn variant="ghost" onClick={() => setDup(false)}>Voltar</Btn>
            </div>
          </Notice>
        )}
        {atyp && (
          <Notice kind="error">
            <span data-atypical-confirm-text>A taxa efetiva calculada está fora da faixa de referência (0,01 a 1.000 BRL por 1 USD). Os valores informados serão gravados como estão e a confirmação ficará registrada na auditoria. Deseja gravar mesmo assim?</span>
            <div className="row" style={{ marginTop: 8 }}>
              <Btn variant="danger" onClick={(e) => { setAtyp(null); void submit(e as unknown as FormEvent, false, true); }} data-atypical-confirm>Confirmar taxa atípica e gravar</Btn>
              <Btn variant="ghost" onClick={() => setAtyp(null)}>Revisar valores</Btn>
            </div>
          </Notice>
        )}
        <p className="small muted" style={{ marginTop: 0 }}>Movimenta valores entre contas da mesma entidade. Não é receita nem despesa e não entra no resultado. Tarifas, IOF e diferenças de câmbio, se houver, devem ser lançados à parte como despesa.</p>
        <div className="grid2">
          <Field label="Conta de origem" required><Select value={fromId} onChange={(e) => setFromId(e.target.value)} name="fromAccount">{accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}</Select></Field>
          <Field label="Conta de destino" required><Select value={toId} onChange={(e) => setToId(e.target.value)} name="toAccount">{accounts.filter((a) => a.id !== fromId).map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}</Select></Field>
        </div>
        <div className="grid2">
          <Field label={`Valor debitado na origem (${from?.currency ?? "—"})`} required><Input value={fromAmount} onChange={(e) => setFromAmount(e.target.value)} inputMode="decimal" decimals={2} placeholder="0,00" name="fromAmount" /></Field>
          {cross
            ? <Field label={`Valor creditado no destino (${to?.currency})`} required><Input value={toAmount} onChange={(e) => setToAmount(e.target.value)} inputMode="decimal" decimals={2} placeholder="0,00" name="toAmount" /></Field>
            : <Field label={`Valor creditado no destino (${to?.currency ?? "—"})`} required><Input value={fromAmount} readOnly aria-readonly name="toAmountSame" /></Field>}
        </div>
        {cross && <p className="small muted" style={{ margin: "-4px 0 8px" }}>Informe os dois valores como constam no extrato do banco ou da corretora. A taxa efetiva é calculada automaticamente.</p>}
        {quote?.crossCurrency && <RateInfo rateDisplay={quote.rateDisplay} unit={quote.rateUnit} />}
        {quote?.atypicalRate && <Notice kind="error"><span data-atypical-alert>Taxa atípica: fora da faixa de referência (0,01 a 1.000 BRL por 1 USD). Confira se os valores estão certos; você poderá confirmar e gravar mesmo assim.</span></Notice>}
        {quoteErr && <Notice kind="error">{quoteErr}</Notice>}
        <Field label="Descrição"><Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} autoComplete="off" name="description" /></Field>
        {!transfer && rep.fields({ norm: fromNorm, format: (v) => formatMoney(v.toFixed(2), from?.currency ?? "BRL") })}
        {transfer?.seriesId && <p className="rep-info" data-scope-box>{transfer.seriesKind === "parcelada" ? `Parcela ${transfer.installmentNo}/${transfer.installmentTotal}` : transfer.installmentNo ? `Transferência fixa nº ${transfer.installmentNo}${transfer.installmentTotal ? `/${transfer.installmentTotal}` : ""}` : "Transferência fixa"} — ao salvar, vamos perguntar se a alteração vale só para esta ou também para as seguintes.</p>}
        {!lockedSettle && <label className="check"><input type="checkbox" checked={done} onChange={(e) => setDone(e.target.checked)} name="done" /> Já foi realizada</label>}
        <div className="grid2">
          {(!done || transfer) && <Field label="Data prevista" required><Input type="date" value={plannedDate} min={floor} onChange={(e) => setPlannedDate(e.target.value)} name="plannedDate" required /></Field>}
          {done && !lockedSettle && <Field label="Data da transferência (efetiva)" required><Input type="date" value={settledDate} min={floor} max={today()} onChange={(e) => setSettledDate(e.target.value)} name="settledDate" required /></Field>}
        </div>
        <p className="small muted" data-date-limits style={{ margin: "-4px 0 8px" }}>
          {floorAcc && <>A data não pode ser anterior a {fmtDay(floor!)} (saldo inicial da conta «{floorAcc.name}»). </>}
          {done && !lockedSettle ? <>A data efetiva vai até hoje ({fmtDay(today())}): ela registra algo que já aconteceu; pode ser anterior ou posterior à data prevista. </> : <>A data prevista pode ser futura; a data efetiva só é informada quando a transferência acontece (até hoje). </>}
        </p>
        <AttachmentsField realm={realm} ownerType="transfer" ownerId={transfer?.id} pending={pending} setPending={setPending} />
        <Field label="Observações"><Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} name="notes" /></Field>
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><button type="submit" className="btn primary" disabled={busy} data-tr-save>{busy ? "Salvando…" : done && !transfer ? "Registrar" : "Salvar"}</button></div>
      </form>
    </Modal>
      {askScope && <ScopeDialog title="Aplicar a alteração a…" what={`Esta é uma ocorrência de série (${transfer?.seriesKind === "parcelada" ? `parcela ${transfer.installmentNo}/${transfer.installmentTotal}` : "transferência fixa"}). A alteração vale somente para ela ou também para as seguintes ainda planejadas?`} busy={busy} onClose={() => setAskScope(false)} onPick={(sc) => void submit(null, false, false, sc)} />}
    </>
  );
}
